import { useState, useCallback } from 'react';
import { IBookmark, IBookmarkTag } from '@/common/types/IBookmark';
import { webBookmarkService } from '../utilities/bookmark/WebBookmarkService';

export function useBookmarks() {
  const [bookmarks, setBookmarks] = useState<IBookmark[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadBookmarks = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      const data = await webBookmarkService.getAllBookmarks();
      setBookmarks(data);
      setError(null);
    } catch (err) {
      setError('Failed to load bookmarks');
      console.error('Error loading bookmarks:', err);
    } finally {
      setLoading(false);
    }
  };

  const createBookmark = useCallback(async (bookmarkData: Partial<IBookmark>) => {
    try {
      const result = await webBookmarkService.createBookmark(bookmarkData);
      if (result?.id) {
        await loadBookmarks(); // Reload to get the updated list
        return result;
      }
      throw new Error('Failed to create bookmark');
    } catch (err) {
      setError('Failed to create bookmark');
      console.error('Error creating bookmark:', err);
      return undefined;
    }
  }, []);

  const updateBookmark = useCallback(async (bookmark: IBookmark) => {
    try {
      await webBookmarkService.updateBookmark(bookmark);
      await loadBookmarks(false); // Reload to get the updated list
    } catch (err) {
      setError('Failed to update bookmark');
      console.error('Error updating bookmark:', err);
    }
  }, []);

  const updateTags = useCallback(async (
    bookmarkId: string,
    tags: IBookmarkTag[],
    categories?: string[]
  ) => {
    try {
      await webBookmarkService.updateTags(bookmarkId, tags, categories);
      await loadBookmarks(); // Reload to get the updated list
    } catch (err) {
      setError('Failed to update tags');
      console.error('Error updating tags:', err);
    }
  }, []);

  const deleteBookmark = useCallback(async (id: string) => {
    try {
      await webBookmarkService.deleteBookmark(id);
      await loadBookmarks(false); // Reload to get the updated list
    } catch (err) {
      setError('Failed to delete bookmark');
      console.error('Error deleting bookmark:', err);
    }
  }, []);

  return {
    bookmarks,
    loading,
    error,
    createBookmark,
    updateBookmark,
    updateTags,
    deleteBookmark,
    refreshBookmarks: loadBookmarks,
  };
}
